import React, { Fragment, ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, Report, ReportService } from '@philips-emr/hpms-stipulator-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import { RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyOrBlankString, isFunction } from '@philips-emr/tws-utils';

import BaseController from '../BaseController';

export interface Mensalidade {
  iePermiteSegundaVia: string,
  ieVisualizaNotaFiscal: string,
  ieNegociado: string,
  cdVerificacaoNfse: string,
  dsLinkNf: string,
  dsLinkNfsMens: string,
  dsSituacao: string,
  nrSeqMensalidade: number;
  situacaoSuperscription: MensalidadeSituacaoSuperscription,
  nrTitulo: number,
  ieSituacao: string,
}

interface NotaFiscalMensalidade {
  [key: string]: Value,
  cdCgcOperadora: string;
  cdVerificacaoNfse: string;
  dsLinkNfsMens: string,
}

enum MensalidadeSituacaoSuperscription {
  VENCIDO = 'V',
  NEGOCIADO = 'N',
  ABERTO = '1',
  LIQUIDADO = '2',
  CANCELADO = '3',
  LIQUIDADO_CHEQUE = '4',
  TRIBUT_TRANSFERIDO = '5',
  LIQUIDADO_PERDA = '6',
}

const OptionMensalidade = {
  SEGUNDA_VIA: 'segunda-via',
  DETALHAMENTO: 'detalhamento',
  NOTA_FISCAL: 'nota-fiscal',
  OBSERVACOES: 'observacoes',
};

export default class BaseMensalidadeCustomCardController extends BaseController {

  @Inject('fetch-mensalidades')
  protected fetchMensalidade: FetchAPI;

  private reportService: ReportService;

  private exibeDetalhamento: boolean;

  private exibeSegundaVia: boolean;

  protected onMountFetchMensalidades(): void {
    const { nrSeqSubestipulante } = this.context.getBulkData();
    void this.exibirDetalhamento().then((resp: boolean) => this.exibeDetalhamento = resp);
    void this.exibirSegundaVia().then((resp: boolean) => this.exibeSegundaVia = resp);
    const url = nrSeqSubestipulante ? Endpoint.GET.MENSALIDADES_SUBESTIPULANTE : Endpoint.GET.MENSALIDADES;
    this.fetchMensalidade.setEndpoint(url);
  }

  protected onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { situacaoSuperscription } = data as Mensalidade;

    render((): ReactElement => getMensalidadeStamp(situacaoSuperscription));
  }

  protected onCreateOption(event: HpmsCustomCardMenuRenderEvent<Mensalidade>): void {
    const { data, key, shouldRender } = event;

    const {
      dsLinkNf,
      iePermiteSegundaVia,
      ieVisualizaNotaFiscal,
      ieSituacao,
      dsLinkNfsMens,
      cdVerificacaoNfse,
    } = data;

    const show = {
      [OptionMensalidade.SEGUNDA_VIA]: this.exibeSegundaVia && ieSituacao === MensalidadeSituacaoSuperscription.ABERTO && iePermiteSegundaVia === 'S',
      [OptionMensalidade.DETALHAMENTO]: this.exibeDetalhamento,
      [OptionMensalidade.NOTA_FISCAL]: ieVisualizaNotaFiscal === 'S' && (!isEmptyOrBlankString(dsLinkNf) || (!isEmptyOrBlankString(dsLinkNfsMens) && !isEmptyOrBlankString(cdVerificacaoNfse))),
      [OptionMensalidade.OBSERVACOES]: true,
    };

    shouldRender(show[key]);
  }

  protected onOptionClick(event: HpmsCustomCardEvent<Mensalidade>): void {
    const { data, key } = event;
    const { dsLinkNf, nrSeqMensalidade, nrTitulo } = data;

    const action = {
      [OptionMensalidade.SEGUNDA_VIA]: () => this.imprimirSegundaVia(nrTitulo),
      [OptionMensalidade.DETALHAMENTO]: () => this.imprimirDetalhamento(nrSeqMensalidade),
      [OptionMensalidade.NOTA_FISCAL]: () => this.notaFiscal(nrSeqMensalidade, dsLinkNf),
      [OptionMensalidade.OBSERVACOES]: () => this.observacoes(nrSeqMensalidade),
    };

    if (isFunction(action[key])) {
      action[key]();
    }
  }

  private notaFiscal = (nrSeqMensalidade: number, dsLinkNf: string): void => {
    if (!isEmptyOrBlankString(dsLinkNf)) {
      window.open(dsLinkNf, '_blank');
      return;
    }

    const { nrSeqContrato } = this.context.getBulkData();

    void HttpService.get<NotaFiscalMensalidade>(Endpoint.GET.MENSALIDADE_NOTA_FISCAL, { nrSeqMensalidade, nrSeqContrato })
      .then(resp => {
        const { body: nfe } = resp;

        if (nfe && !isEmptyOrBlankString(nfe.cdVerificacaoNfse) && !isEmptyOrBlankString(nfe.dsLinkNfsMens)) {
          ModalService.openDialog({
            title: 'stipulator.nota-fiscal-eletronica',
            fragment: 'mensalidade-nota-fiscal',
            contextData: nfe,
          });
        }
      });
  };

  private observacoes = (nrSeqMensalidade: number): void => {
    const { nrSeqContrato } = this.context.getBulkData();

    void HttpService.get<Record<string, string>>(Endpoint.GET.MENSALIDADE_OBSERVACOES, { nrSeqMensalidade, nrSeqContrato })
      .then(resp => {
        const { body: obs } = resp;

        if (obs) {
          ModalService.openDialog({
            title: 'stipulator.observacoes-mensalidade',
            fragment: 'mensalidade-observacoes',
            contextData: obs,
          });
        }
      });
  };

  private imprimirSegundaVia(nrSequencia: number): void {
    this.reportService = new ReportService('6', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_TITULO: nrSequencia });
  }

  private exibirSegundaVia = async (): Promise<boolean> => {
    const service = new ReportService('6', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    return relatorios.length !== 0;
  };

  private imprimirDetalhamento(nrSequencia: number): void {
    this.reportService = new ReportService('10', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_MENSALIDADE: nrSequencia });
  }

  private exibirDetalhamento = async (): Promise<boolean> => {
    const service = new ReportService('10', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    return relatorios.length !== 0;
  };

}

function getMensalidadeStamp(situacaoSuperscription: MensalidadeSituacaoSuperscription): ReactElement {
  if ([
    MensalidadeSituacaoSuperscription.CANCELADO,
    MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO,
  ].includes(situacaoSuperscription)) {
    return <Fragment />;
  }

  return (
    <Stamp
      color={getMensalidadeIconColor(situacaoSuperscription)}
      icon={getMensalidadeIconType(situacaoSuperscription)}
      label={getMensalidadeStampLabel(situacaoSuperscription)}
    />
  );
}

function getMensalidadeStampLabel(situacaoSuperscription: MensalidadeSituacaoSuperscription): string {
  const labels: Record<string, string> = {
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: 'stipulator.negociado',
    [MensalidadeSituacaoSuperscription.VENCIDO]: 'stipulator.vencido',
  };

  if (situacaoSuperscription in labels) {
    return labels[situacaoSuperscription];
  }

  return format('domain', situacaoSuperscription, { name: 'stipulator.status-mensalidade' });

}

function getMensalidadeIconColor(situacaoSuperscription: MensalidadeSituacaoSuperscription): Color {
  const color = {
    [MensalidadeSituacaoSuperscription.VENCIDO]: Color.SAFETY_RED,
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.ABERTO]: Color.SAFETY_ORANGE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO]: Color.SAFETY_GREEN,
    [MensalidadeSituacaoSuperscription.CANCELADO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_CHEQUE]: Color.SAFETY_GREEN,
    [MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_PERDA]: Color.SAFETY_GREEN,
  };

  return color[situacaoSuperscription];
}

function getMensalidadeIconType(situacaoSuperscription: MensalidadeSituacaoSuperscription): StampIcon {
  const icon = {
    [MensalidadeSituacaoSuperscription.VENCIDO]: StampIcon.CANCELED,
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.ABERTO]: StampIcon.WARNING,
    [MensalidadeSituacaoSuperscription.LIQUIDADO]: StampIcon.CHECK,
    [MensalidadeSituacaoSuperscription.CANCELADO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_CHEQUE]: StampIcon.CHECK,
    [MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_PERDA]: StampIcon.CHECK,
  };

  return icon[situacaoSuperscription];
}
